import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Camera, Upload, Trash2, X, ZoomIn, Image as ImageIcon, Star, Plus, MoveUp, MoveDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const PortfolioGallery = ({ userId, portfolioImages = [], portfolioImageKeys = [], isEditMode = false, isDark = false, onImagesUpdate }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deletingImage, setDeletingImage] = useState(null);

  // Local ordered copy so HTML5 drag feels instant; mirrors props, persisted via the real reorder endpoint.
  const [items, setItems] = useState([]);
  useEffect(() => {
    setItems(portfolioImages.map((url, i) => ({ url, key: portfolioImageKeys[i] })));
  }, [portfolioImages, portfolioImageKeys]);
  const dragFrom = useRef(null);
  const [dragging, setDragging] = useState(null);

  const openLightbox = useCallback((imageUrl, index) => {
    setSelectedImage(imageUrl);
    setSelectedImageIndex(index);
  }, []);

  const closeLightbox = useCallback(() => {
    setSelectedImage(null);
    setSelectedImageIndex(null);
  }, []);

  const navigateImage = useCallback((direction) => {
    if (selectedImageIndex === null) return;

    let newIndex;
    if (direction === 'prev') {
      newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : portfolioImages.length - 1;
    } else {
      newIndex = selectedImageIndex < portfolioImages.length - 1 ? selectedImageIndex + 1 : 0;
    }

    setSelectedImageIndex(newIndex);
    setSelectedImage(portfolioImages[newIndex]);
  }, [selectedImageIndex, portfolioImages]);

  const handleKeyPress = useCallback((e) => {
    if (!selectedImage) return;
    if (e.key === 'ArrowLeft') navigateImage('prev');
    if (e.key === 'ArrowRight') navigateImage('next');
    if (e.key === 'Escape') closeLightbox();
  }, [selectedImage, navigateImage, closeLightbox]);

  // Add keyboard listener
  useEffect(() => {
    if (selectedImage) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [selectedImage, handleKeyPress]);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate: JPG only, 2MB max, 15 images max
    const allowedTypes = ['image/jpeg'];
    const maxSize = 3 * 1024 * 1024; // 3MB
    const maxImages = 15;

    if (portfolioImages.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed. You can upload ${maxImages - portfolioImages.length} more.`);
      return;
    }

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Only JPG format allowed`);
        return;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large. Maximum 3MB per image`);
        return;
      }
    }

    setUploadingImages(true);
    try {
      const uploadedKeys = [];

      for (const file of files) {
        // Step 1: Get presigned URL
        const uploadUrlResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile/portfolio/upload-url`,
          null,
          {
            params: {
              user_id: userId,
              filename: file.name,
              content_type: file.type
            }
          }
        );

        const { url, fields, object_key } = uploadUrlResponse.data;

        // Step 2: Upload to S3
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          formData.append(key, value);
        });
        formData.append('file', file);

        await axios.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        uploadedKeys.push(object_key);
      }

      // Step 3: Confirm all uploads
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/portfolio/confirm`,
        {
          user_id: userId,
          object_keys: uploadedKeys
        }
      );

      toast.success(`${files.length} image${files.length > 1 ? 's' : ''} uploaded successfully!`);
      if (onImagesUpdate) onImagesUpdate();
    } catch (error) {
      console.error('Portfolio upload error:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageKey, index) => {
    if (!window.confirm('Delete this portfolio image?')) return;

    setDeletingImage(imageKey);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/portfolio/${userId}`,
        {
          params: { object_key: imageKey }
        }
      );

      toast.success('Image removed');
      if (onImagesUpdate) onImagesUpdate();
    } catch (error) {
      console.error('Delete image error:', error);
      toast.error('Failed to delete image');
    } finally {
      setDeletingImage(null);
    }
  };

  // Persist an arbitrary order to the REAL reorder endpoint.
  const persistOrder = async (ordered) => {
    const orderedKeys = ordered.map((it) => it.key).filter(Boolean);
    if (orderedKeys.length !== portfolioImageKeys.length) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/profile/portfolio/reorder`,
        {
          user_id: userId,
          ordered_keys: orderedKeys
        }
      );
      if (onImagesUpdate) onImagesUpdate();
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to reorder images');
      // revert to props order on failure
      setItems(portfolioImages.map((url, i) => ({ url, key: portfolioImageKeys[i] })));
    }
  };

  const handleReorderImage = async (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;
    const next = [...items];
    [next[index], next[newIndex]] = [next[newIndex], next[index]];
    setItems(next);
    toast.success('Images reordered');
    persistOrder(next);
  };

  // ----- HTML5 drag reorder (within the grid) -----
  const onTileDragStart = (index) => (e) => {
    dragFrom.current = index;
    setDragging(index);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', String(index)); } catch (_) {}
    }
  };
  const onTileDragOver = (index) => (e) => {
    e.preventDefault();
    const from = dragFrom.current;
    if (from === null || from === index) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });
    dragFrom.current = index;
    setDragging(index);
  };
  const onTileDragEnd = () => {
    dragFrom.current = null;
    setDragging(null);
    persistOrder(items);
  };

  const setAsCover = (index) => {
    if (index === 0) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.unshift(moved);
    setItems(next);
    toast.success('Set as cover');
    persistOrder(next);
  };

  // ============================ DARK (Command Deck) ============================
  if (isDark) {
    const cardStyle = { background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.09)', borderRadius: 18, padding: '22px 24px' };
    const count = items.length;

    // ----- EDIT: drag-to-reorder grid with cover/delete + add tile -----
    if (isEditMode) {
      return (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.6px', color: 'rgba(255,255,255,.4)' }}>PORTFOLIO · DRAG TO REORDER</div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,.4)' }}>{count} {count === 1 ? 'image' : 'images'}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 9 }}>
            {items.map((it, idx) => (
              <div
                key={it.key || idx}
                data-tile
                draggable="true"
                onDragStart={onTileDragStart(idx)}
                onDragOver={onTileDragOver(idx)}
                onDragEnd={onTileDragEnd}
                style={{ position: 'relative', cursor: 'grab', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', background: '#1a2033', opacity: dragging === idx ? 0.4 : 1, transition: 'opacity .15s ease' }}
              >
                <img src={it.url} alt={`Portfolio ${idx + 1}`} draggable="false" style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
                {idx === 0 ? (
                  <span style={{ position: 'absolute', top: 6, left: 6, display: 'flex', alignItems: 'center', gap: 4, fontSize: 9.5, fontWeight: 700, color: '#0C1120', background: '#FCD34D', padding: '3px 7px', borderRadius: 999 }}>★ Cover</span>
                ) : (
                  <button type="button" title="Set as cover" onClick={(e) => { e.stopPropagation(); setAsCover(idx); }}
                    style={{ position: 'absolute', top: 6, left: 6, width: 22, height: 22, borderRadius: 7, background: 'rgba(12,17,32,.7)', border: 'none', color: '#FCD34D', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Star size={12} fill="currentColor" stroke="none" />
                  </button>
                )}
                {it.key && (
                  <button type="button" title="Remove" disabled={deletingImage === it.key} onClick={(e) => { e.stopPropagation(); handleDeleteImage(it.key, idx); }}
                    style={{ position: 'absolute', top: 6, right: 6, width: 22, height: 22, borderRadius: 7, background: 'rgba(12,17,32,.7)', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: deletingImage === it.key ? 0.5 : 1 }}>
                    <X size={11} strokeWidth={2.4} />
                  </button>
                )}
              </div>
            ))}
            {/* Add tile */}
            <label style={{ aspectRatio: '1', borderRadius: 12, border: '1.5px dashed rgba(255,255,255,.18)', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#28C0D0', cursor: count >= 15 ? 'not-allowed' : 'pointer', opacity: count >= 15 ? 0.45 : 1 }}>
              <input type="file" accept="image/jpeg" multiple onChange={handleFileSelect} style={{ display: 'none' }} disabled={uploadingImages || count >= 15} />
              {uploadingImages ? <Upload size={20} /> : <Plus size={20} strokeWidth={2.2} />}
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>{uploadingImages ? 'Uploading…' : 'Add'}</span>
            </label>
          </div>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,.4)', marginTop: 8 }}>Drag to reorder. ★ sets the cover. Up to 15 images, JPG only, 3MB max.</p>
        </>
      );
    }

    // ----- VIEW: 4-col gallery, first tile tagged Cover, tiles open lightbox -----
    if (count === 0) {
      return (
        <div style={cardStyle}>
          <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff', marginBottom: 10 }}>Portfolio</div>
          <div style={{ textAlign: 'center', padding: '28px 0', color: 'rgba(255,255,255,.45)' }}>
            <ImageIcon size={40} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
            <p style={{ fontSize: 13 }}>No portfolio images yet</p>
          </div>
        </div>
      );
    }
    return (
      <>
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div className="cd-display" style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Portfolio</div>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,.5)', background: 'rgba(255,255,255,.06)', padding: '3px 10px', borderRadius: 999 }}>{count} {count === 1 ? 'image' : 'images'}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
            {items.map((it, idx) => (
              <div key={it.key || idx} onClick={() => openLightbox(it.url, idx)}
                style={{ cursor: 'pointer', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', background: '#1a2033', position: 'relative', display: 'flex', alignItems: 'flex-end', padding: 8 }}>
                <img src={it.url} alt={`Portfolio ${idx + 1}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }} />
                {idx === 0 && (
                  <span style={{ position: 'relative', zIndex: 1, fontSize: 9, fontWeight: 700, color: '#fff', background: 'rgba(0,0,0,.45)', padding: '2px 7px', borderRadius: 999 }}>★ Cover</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {selectedImage && createPortal(
          <div onMouseDown={closeLightbox}
            style={{ position: 'fixed', inset: 0, zIndex: 4100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'rgba(6,8,14,.86)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>
            <div onMouseDown={(e) => e.stopPropagation()} style={{ position: 'relative', width: '100%', maxWidth: 720 }}>
              <button onClick={closeLightbox} aria-label="Close"
                style={{ position: 'absolute', top: 16, right: 16, width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 3 }}>
                <X size={17} />
              </button>
              <div style={{ aspectRatio: '3 / 2', width: '100%', borderRadius: 16, overflow: 'hidden', background: '#1a2033', boxShadow: '0 40px 90px -30px rgba(0,0,0,.9)' }}>
                <img src={selectedImage} alt={`Portfolio ${selectedImageIndex + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)' }}>Portfolio · <span style={{ color: '#fff' }}>{selectedImageIndex + 1} of {portfolioImages.length}</span></div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }} aria-label="Previous"
                    style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.16)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); navigateImage('next'); }} aria-label="Next"
                    style={{ width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.16)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </>
    );
  }

  // ============================ LIGHT (legacy) ============================
  if (!portfolioImages || portfolioImages.length === 0) {
    return (
      <Card className="bg-white border-gray-200 shadow-xl">
        <CardHeader className="border-b border-gray-200/10">
          <div className="flex items-center justify-between">
            <CardTitle className="text-gray-900">
              Portfolio Gallery
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {isEditMode ? (
            <div className="text-center py-12">
              <Camera className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-4 text-gray-500">
                No portfolio images yet
              </p>
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploadingImages}
                />
                <Button asChild disabled={uploadingImages}>
                  <span className="bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white">
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingImages ? 'Uploading...' : 'Upload Portfolio Images'}
                  </span>
                </Button>
              </label>
              <p className="text-xs text-gray-500 mt-3">
                Upload up to 15 images. Max 3MB each. JPG only. First image shown in profile header.
              </p>
            </div>
          ) : (
            <div className="text-center py-8">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p className="font-medium text-gray-500">No portfolio images</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white border-gray-200 shadow-xl">
        <CardHeader className="border-b border-gray-200/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-100">
                <Camera className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-gray-900">
                  Portfolio Gallery
                </CardTitle>
                <Badge className="mt-1 bg-purple-500/20 text-purple-400 border-purple-400/30 text-xs">
                  {portfolioImages.length} {portfolioImages.length === 1 ? 'image' : 'images'}
                </Badge>
              </div>
            </div>
            {isEditMode && (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  disabled={uploadingImages || portfolioImages.length >= 15}
                />
                <Button asChild size="sm" disabled={uploadingImages || portfolioImages.length >= 15}>
                  <span className="bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white">
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingImages ? 'Uploading...' : 'Add Images'}
                  </span>
                </Button>
              </label>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {portfolioImages.map((imageUrl, idx) => (
              <div
                key={idx}
                className="relative group aspect-square rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 cursor-pointer"
                onClick={() => !isEditMode && openLightbox(imageUrl, idx)}
              >
                <img
                  src={imageUrl}
                  alt={`Portfolio ${idx + 1}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  onError={(e) => {
                    console.error('Image load error:', imageUrl);
                    e.target.src = 'https://via.placeholder.com/400?text=Image+Not+Found';
                  }}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                  {!isEditMode && <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />}
                </div>
                {isEditMode && (
                  <div className="absolute top-2 left-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {idx > 0 && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleReorderImage(idx, 'up'); }} className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded shadow-lg" title="Move up">
                        <MoveUp className="w-4 h-4" />
                      </button>
                    )}
                    {idx < portfolioImages.length - 1 && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleReorderImage(idx, 'down'); }} className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded shadow-lg" title="Move down">
                        <MoveDown className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
                {isEditMode && portfolioImageKeys[idx] && (
                  <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteImage(portfolioImageKeys[idx], idx); }} disabled={deletingImage === portfolioImageKeys[idx]} className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg disabled:opacity-50">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <div className={`absolute bottom-2 left-2 ${idx === 0 ? 'bg-purple-600' : 'bg-black/60'} backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-md`}>
                  {idx === 0 ? '★ Cover' : idx + 1}
                </div>
              </div>
            ))}
          </div>
          {isEditMode && (
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                <strong>First image is your cover photo.</strong> Use arrows to reorder. Click trash to delete. Max 15 images, JPG only, 3MB max.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedImage && (
        <div className="fixed inset-0 bg-black/95 backdrop-blur-md flex items-center justify-center z-[200] top-0 left-0 right-0 bottom-0" onClick={closeLightbox}>
          <button onClick={closeLightbox} className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all z-10">
            <X className="w-6 h-6" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-4 rounded-full transition-all z-10 backdrop-blur-sm">
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); navigateImage('next'); }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-4 rounded-full transition-all z-10 backdrop-blur-sm">
            <ChevronRight className="w-8 h-8" />
          </button>
          <div className="w-[90%] h-[90%] flex items-center justify-center">
            <img src={selectedImage} alt={`Portfolio ${selectedImageIndex + 1} of ${portfolioImages.length}`} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-full">
            <span className="text-sm font-medium">{selectedImageIndex + 1} / {portfolioImages.length}</span>
          </div>
        </div>
      )}
    </>
  );
};

export default PortfolioGallery;
